<template>
  <div>

    <Modal
      :title="isExistingParcs ? $t('titles.edit_parcs') : $t('titles.create_parcs')"
      :submit-button="canWriteLocations"
      :submit="submit"
      :submitting="submitting"
    >

      <template v-if="isExistingParcs">

        <o-select
          label="Company"
          v-model="v$.companyName.$model"
          readonly
          :items="objectToItems(ParcsCompanies)"
        />

        <o-text-field
          :label="$t('titles.external_id')"
          readonly
          v-model="v$.externalID.$model"
        />

      </template>

      <template v-if="!isExistingParcs">

        <o-select
          :label="$t('titles.company')"
          v-model="v$.companyName.$model"
          :validator="v$.companyName"
          :items="objectToItems(ParcsCompanies)"
          :readonly="!canWriteLocations"
        />

        <o-text-field
          :label="$t('titles.external_id')"
          v-model="v$.externalID.$model"
          :validator="v$.externalID"
          :readonly="!canWriteLocations"
        />

      </template>

      <o-autocomplete
        :label="$t('titles.channels')"
        multiple
        v-model="v$.channels.$model"
        :items="ChannelRegistry.getOptions()"
      />

      <o-switch
        v-if="canWriteLocations"
        :label="$t('misc.live')"
        v-model="v$.live.$model"
      />

      <o-text-field
        v-if="!canWriteLocations"
        :label="$t('misc.live')"
        :model-value="v$.live.$model ? $t('misc.yes') : $t('misc.no')"
        readonly
      />

    </Modal>

  </div>
</template>

<script setup lang="ts">

import { inject, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// components
import OAutocomplete from '../../../forms/OAutocomplete.vue'

// models
import Parcs from '../../../../models/Parcs'

// validation
import useVuelidate from '@vuelidate/core'
import { required } from '../../../../utils/i18n-validators'

// enums
import { ParcsCompanies } from '../../../../enums'

// misc
import objectToItems from '../../../../utils/objectToItems'
import NotificationService from '../../../../services/NotificationService'
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import type { Can } from '../../../../main'
import ChannelRegistry from '../../../../channels/ChannelRegistry'

interface Props {
  parcs: Parcs
}

const props = defineProps<Props>()

const isExistingParcs = props.parcs.ID
const { t } = useI18n()

const $can = inject('$can') as Can
const canWriteLocations = $can('locations.write')

/**
 * Form state and rules
 */

const formState = reactive({
  companyName: props.parcs.companyName,
  externalID: props.parcs.externalID,
  channels: props.parcs.channels,
  live: props.parcs.live,
})

const formRules = {
  companyName: { required },
  externalID: { required },
  channels: {},
  live: {},
}

const v$ = useVuelidate(formRules, formState)

/**
 * Submission
 */

const submitting = ref<boolean>(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid || !canWriteLocations) {
    return
  }

  submitting.value = true
  let promise

  if (isExistingParcs) {
    promise = Parcs.saveExisting(props.parcs.ID, {
      // companyName: formState.companyName, // TODO: omit this?
      // externalID: formState.externalID, // TODO: this isn't picking up the correct value. but we might want to omit it anyway?
      channels: formState.channels,
      live: formState.live,
    })
  } else {
    promise = Parcs.saveNew({
      locationID: props.parcs.locationID,
      companyName: formState.companyName,
      externalID: formState.externalID,
      channels: formState.channels,
      live: formState.live,
    })
  }

  promise
    .then(() => {
      const msg = isExistingParcs ? t('alerts.parcs_updated') : t('alerts.parcs_created')
      NotificationService.success(msg)
      closeDialog(null)
    })
    .catch((error) => {
      console.error(error)
      const msg = isExistingParcs ? t('alerts.couldnt_update_parcs') : t('alerts.couldnt_create_parcs')
      NotificationService.error(msg)
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
